import { useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap, AttributionControl } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import Button from '../components/ui/Button'
import { listTrucks, getTruckTrips, getTruckHistory, getLocations } from '../api/tankguard'

const TEAL = '#14B8A6'
const FALLBACK_CENTER = [49.0, 10.0]
const FALLBACK_ZOOM = 6

// Distinct colours per trip so adjacent ones can be told apart on the map.
const TRIP_COLORS = ['#0d9488', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2', '#dc2626']

function makePin(color, size = 18) {
  return L.divIcon({
    html: `<div style="position:relative">
      <div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);position:relative;z-index:1"></div>
    </div>`,
    className: '', iconSize: [size, size], iconAnchor: [size / 2, size / 2],
  })
}

function makeEventIcon(emoji, bg) {
  return L.divIcon({
    html: `<div style="width:22px;height:22px;border-radius:50%;background:${bg};border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-size:12px;box-shadow:0 2px 6px rgba(0,0,0,0.3)">${emoji}</div>`,
    className: '', iconSize: [22, 22], iconAnchor: [11, 11],
  })
}

const startPin = makePin('#16a34a', 20)
const endPin = makePin('#dc2626', 20)
const theftIcon = makeEventIcon('!', '#dc2626')
const refuelIcon = makeEventIcon('⛽', '#0d9488')
const stopIcon = (durationMin) => L.divIcon({
  html: `<div style="background:rgba(75,85,99,0.92);color:#fff;border:2px solid #fff;border-radius:14px;padding:2px 7px;font-size:10px;font-weight:600;box-shadow:0 2px 5px rgba(0,0,0,0.25);white-space:nowrap">${durationMin}m</div>`,
  className: '', iconSize: [40, 20], iconAnchor: [20, 10],
})
// Known fuel-station stops get a pump glyph so the eye picks them out of a row
// of grey stop chips. The duration still shows up in the popup.
const fuelStationIcon = L.divIcon({
  html: `<div style="width:26px;height:26px;border-radius:50%;background:#0d9488;border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-size:14px;color:#fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)">⛽</div>`,
  className: '', iconSize: [26, 26], iconAnchor: [13, 13],
})

function haversineM(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function MapBounds({ bounds }) {
  const map = useMap()
  useEffect(() => {
    if (bounds && bounds.length >= 2) {
      try { map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 }) } catch { /* empty bounds */ }
    }
  }, [bounds, map])
  return null
}

function todayUtc() {
  const d = new Date()
  return d.toISOString().slice(0, 10)
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}
function fmtDuration(min) {
  if (min < 60) return min + 'm'
  const h = Math.floor(min / 60)
  const m = min % 60
  return h + 'h ' + m + 'm'
}

// Picks the closest fuel/theft/refuel event near a given timestamp from history.
// We display ledger events extracted from the day's history on the map directly.
function buildEventMarkers(history, trips) {
  // Detect refuel/theft events from history. Same heuristic as backend ledger
  // but kept lean so we don't ship a second TB fetch.
  const events = []
  const speeds = (history.speed || []).map(p => ({ ts: +p.ts, val: parseFloat(p.value) })).sort((a, b) => a.ts - b.ts)
  const lats = (history.latitude || []).map(p => ({ ts: +p.ts, val: parseFloat(p.value) })).sort((a, b) => a.ts - b.ts)
  const lngs = (history.longitude || []).map(p => ({ ts: +p.ts, val: parseFloat(p.value) })).sort((a, b) => a.ts - b.ts)
  const f1 = history.fuel1_level_liters
  const f2 = history.fuel2_level_liters
  const single = history.fuel_level_liters
  let fuel = []
  if (single) {
    fuel = single.map(p => ({ ts: +p.ts, val: parseFloat(p.value) })).filter(p => !isNaN(p.val)).sort((a, b) => a.ts - b.ts)
  } else if (f1 && f2) {
    const aa = f1.map(p => ({ ts: +p.ts, val: parseFloat(p.value) })).filter(p => !isNaN(p.val)).sort((a, b) => a.ts - b.ts)
    const bb = f2.map(p => ({ ts: +p.ts, val: parseFloat(p.value) })).filter(p => !isNaN(p.val)).sort((a, b) => a.ts - b.ts)
    const n = Math.min(aa.length, bb.length)
    for (let i = 0; i < n; i++) fuel.push({ ts: aa[i].ts, val: aa[i].val + bb[i].val })
  }
  const findLatLng = (ts) => {
    const la = lats.find(p => Math.abs(p.ts - ts) < 180000)
    const lo = lngs.find(p => Math.abs(p.ts - ts) < 180000)
    if (!la || !lo) return null
    return [la.val, lo.val]
  }
  for (let i = 1; i < fuel.length; i++) {
    const diff = fuel[i].val - fuel[i - 1].val
    if (diff >= 20) {
      const loc = findLatLng(fuel[i].ts)
      if (loc) events.push({ type: 'refuel', ts: fuel[i].ts, liters: diff, lat: loc[0], lng: loc[1] })
    } else if (diff <= -10) {
      const sp = speeds.find(s => Math.abs(s.ts - fuel[i].ts) < 120000)?.val ?? 0
      if (sp < 2) {
        const loc = findLatLng(fuel[i].ts)
        if (loc) events.push({ type: 'theft', ts: fuel[i].ts, liters: Math.abs(diff), lat: loc[0], lng: loc[1] })
      }
    }
  }
  return events
}

export default function RouteHistory() {
  const [trucks, setTrucks] = useState([])
  const [truckId, setTruckId] = useState(null)
  const [date, setDate] = useState(todayUtc())
  const [trips, setTrips] = useState([])
  const [stops, setStops] = useState([])
  const [locations, setLocations] = useState([])
  const [history, setHistory] = useState({})
  const [highlightTrip, setHighlightTrip] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const loadedKey = useRef('')

  useEffect(() => {
    listTrucks().then(r => {
      setTrucks(r || [])
      if (r && r.length && !truckId) setTruckId(r[0].id)
    }).catch(e => { console.error(e); Sentry.captureException(e) })
    getLocations().then(r => setLocations(r || [])).catch(() => { /* not fatal */ })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function load(id, d) {
    if (!id || !d) return
    const key = id + ':' + d
    if (loadedKey.current === key) return
    loadedKey.current = key
    setLoading(true)
    setErr('')
    setHighlightTrip(null)
    try {
      const [tripsR, histR] = await Promise.all([
        getTruckTrips(id, d),
        getTruckHistory(id, { date: d }),
      ])
      setTrips(tripsR?.trips || [])
      setStops(tripsR?.stops || [])
      setHistory(histR?.history || {})
    } catch (e) {
      console.error(e); Sentry.captureException(e)
      setErr(e.message || 'Failed to load route')
      setTrips([]); setStops([]); setHistory({})
    }
    setLoading(false)
  }

  // Auto-load when truck or date changes. The ref guards against StrictMode
  // double-fire and re-renders that haven't actually changed anything.
  useEffect(() => { load(truckId, date) }, [truckId, date])

  const events = useMemo(() => buildEventMarkers(history, trips), [history, trips])

  // Annotate stops with the nearest known location (within radius_m) so the
  // popup can show "Refueled at FuelHub Friedrichshafen" instead of "Stop".
  const annotatedStops = useMemo(() => {
    if (!stops.length) return []
    return stops.map(s => {
      let best = null, bestDist = Infinity
      for (const loc of locations) {
        const d = haversineM(s.lat, s.lng, Number(loc.lat), Number(loc.lng))
        if (d <= (loc.radius_m || 200) && d < bestDist) { bestDist = d; best = loc }
      }
      return best ? { ...s, location_name: best.name, location_type: best.location_type } : s
    })
  }, [stops, locations])

  const bounds = useMemo(() => {
    const all = []
    trips.forEach(t => t.track?.forEach(p => all.push(p)))
    if (!all.length) return null
    return all
  }, [trips])

  const mapCenter = bounds && bounds.length ? bounds[0] : FALLBACK_CENTER
  const selectedTruck = trucks.find(t => t.id === truckId)

  const total = useMemo(() => {
    let km = 0, fuel = 0, dur = 0
    trips.forEach(t => {
      km += t.distance_km || 0
      fuel += t.fuel_used_l || 0
      dur += t.duration_min || 0
    })
    return {
      km: Math.round(km * 10) / 10,
      fuel: Math.round(fuel * 10) / 10,
      duration: dur,
      trips: trips.length,
    }
  }, [trips])

  return (
    <div className="page">
      {/* Date picker + truck selector */}
      <div className="tg-card" style={{ padding: 'var(--tg-space-4)', marginBottom: 'var(--tg-space-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label className="tg-label" style={{ marginBottom: 4 }}>Truck</label>
            <select
              className="tg-input"
              style={{ minWidth: 200, appearance: 'auto' }}
              value={truckId || ''}
              onChange={e => setTruckId(e.target.value ? parseInt(e.target.value) : null)}
            >
              {trucks.length === 0 && <option value="">No trucks in fleet</option>}
              {trucks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label className="tg-label" style={{ marginBottom: 4 }}>Date</label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <Button size="sm" variant="ghost" onClick={() => setDate(d => addDays(d, -1))} aria-label="Previous day">←</Button>
              <input
                type="date"
                className="tg-input"
                style={{ width: 160 }}
                value={date}
                max={todayUtc()}
                onChange={e => setDate(e.target.value || todayUtc())}
              />
              <Button size="sm" variant="ghost" onClick={() => setDate(d => addDays(d, 1))} disabled={date >= todayUtc()} aria-label="Next day">→</Button>
              <Button size="sm" variant="ghost" onClick={() => setDate(todayUtc())} disabled={date === todayUtc()}>Today</Button>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => { loadedKey.current = ''; load(truckId, date) }}
            loading={loading}
            disabled={!truckId || loading}
          >
            {loading ? 'Loading…' : 'Reload'}
          </Button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 18, alignItems: 'center', fontSize: 12, color: 'var(--tg-text-secondary)' }}>
            <div><strong style={{ color: 'var(--tg-text-primary)' }}>{total.trips}</strong> trips</div>
            <div><strong style={{ color: 'var(--tg-text-primary)' }}>{total.km} km</strong></div>
            <div><strong style={{ color: 'var(--tg-text-primary)' }}>{total.fuel} L</strong> used</div>
            <div><strong style={{ color: 'var(--tg-text-primary)' }}>{fmtDuration(total.duration)}</strong> driving</div>
          </div>
        </div>
        {err && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'var(--tg-danger-50)', border: '1px solid var(--tg-danger-100)', borderRadius: 6, color: 'var(--tg-danger-700)', fontSize: 12 }}>{err}</div>
        )}
      </div>

      {/* Route map */}
      <div className="tg-card" style={{ marginBottom: 'var(--tg-space-4)', overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--tg-border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--tg-text-primary)' }}>
            Route — {selectedTruck?.name || '—'} · {date}
          </span>
          <span style={{ fontSize: 10, color: 'var(--tg-text-muted)' }}>
            Green = start · Red = end · ⛽ fuel station · grey chip = stop · ! theft
          </span>
        </div>
        <div style={{ height: 400 }}>
          <MapContainer center={mapCenter} zoom={FALLBACK_ZOOM} attributionControl={false} style={{ height: '100%', width: '100%' }}>
            <AttributionControl position="bottomleft" prefix={false} />
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapBounds bounds={bounds} />
            {trips.map((t, i) => {
              const color = TRIP_COLORS[i % TRIP_COLORS.length]
              const isHighlight = highlightTrip == null || highlightTrip === t.trip_index
              return t.track && t.track.length > 1 ? (
                <Polyline
                  key={t.trip_index}
                  positions={t.track}
                  color={color}
                  weight={isHighlight ? 5 : 2.5}
                  opacity={isHighlight ? 0.95 : 0.4}
                  eventHandlers={{ click: () => setHighlightTrip(t.trip_index) }}
                />
              ) : null
            })}
            {trips.length > 0 && trips[0].start_lat != null && (
              <Marker position={[trips[0].start_lat, trips[0].start_lng]} icon={startPin}>
                <Popup>Start · {fmtTime(trips[0].start_ts)}</Popup>
              </Marker>
            )}
            {trips.length > 0 && trips[trips.length - 1].end_lat != null && (
              <Marker position={[trips[trips.length - 1].end_lat, trips[trips.length - 1].end_lng]} icon={endPin}>
                <Popup>End · {fmtTime(trips[trips.length - 1].end_ts)}</Popup>
              </Marker>
            )}
            {events.map((ev, i) => (
              <Marker key={'ev' + i} position={[ev.lat, ev.lng]} icon={ev.type === 'theft' ? theftIcon : refuelIcon}>
                <Popup>
                  <strong>{ev.type === 'theft' ? 'Theft' : 'Refuel'}</strong><br />
                  {fmtTime(ev.ts)} · {ev.liters.toFixed(1)} L
                </Popup>
              </Marker>
            ))}
            {annotatedStops.map((s, i) => (
              s.lat != null && s.lng != null ? (
                <Marker
                  key={'stop' + i}
                  position={[s.lat, s.lng]}
                  icon={s.location_type === 'fuel_station' ? fuelStationIcon : stopIcon(s.duration_min)}
                >
                  <Popup>
                    <strong>{s.location_name || (s.location_type === 'fuel_station' ? 'Fuel station' : 'Stop')}</strong><br />
                    {fmtTime(s.start_ts)} · {s.duration_min} min
                    {s.fuel_at_stop != null && <><br />Fuel: {s.fuel_at_stop} L</>}
                  </Popup>
                </Marker>
              ) : null
            ))}
          </MapContainer>
        </div>
      </div>

      {/* Trip list */}
      <div className="tg-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--tg-border-default)' }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--tg-text-primary)' }}>Trips on {date}</span>
        </div>
        {loading && (
          <div style={{ padding: 20, color: 'var(--tg-text-muted)', fontSize: 13 }}>Loading trips…</div>
        )}
        {!loading && trips.length === 0 && !err && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--tg-text-muted)', fontSize: 13 }}>
            No trips recorded on this day.
          </div>
        )}
        {!loading && trips.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--tg-bg-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--tg-text-tertiary)', fontSize: 10 }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 500 }}>#</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 500 }}>Start</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 500 }}>End</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 500 }}>Distance</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 500 }}>Duration</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 500 }}>Avg</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 500 }}>Max</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 500 }}>Fuel</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 500 }}>L/100km</th>
              </tr>
            </thead>
            <tbody>
              {trips.map((t, i) => {
                const isHighlight = highlightTrip === t.trip_index
                const color = TRIP_COLORS[i % TRIP_COLORS.length]
                return (
                  <tr
                    key={t.trip_index}
                    onClick={() => setHighlightTrip(isHighlight ? null : t.trip_index)}
                    style={{
                      borderTop: '1px solid var(--tg-border-default)',
                      background: isHighlight ? 'var(--tg-teal-50)' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <td style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }} />
                      <span style={{ color: 'var(--tg-text-secondary)' }}>{t.trip_index}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--tg-text-primary)' }}>{fmtTime(t.start_ts)}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--tg-text-primary)' }}>{fmtTime(t.end_ts)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500 }}>{(t.distance_km || 0).toFixed(1)} km</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmtDuration(t.duration_min)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{Math.round(t.avg_speed_kmh || 0)} km/h</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--tg-text-muted)' }}>{Math.round(t.max_speed_kmh || 0)} km/h</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{t.fuel_used_l != null ? t.fuel_used_l.toFixed(1) + ' L' : '—'}</td>
                    <td style={{
                      padding: '10px 12px',
                      textAlign: 'right',
                      color: t.efficiency_l_per_100km
                        ? (t.efficiency_l_per_100km > 30 ? 'var(--tg-status-danger)' : t.efficiency_l_per_100km > 20 ? 'var(--tg-status-warning)' : 'var(--tg-status-online)')
                        : 'var(--tg-text-muted)',
                      fontWeight: 500,
                    }}>
                      {t.efficiency_l_per_100km != null ? t.efficiency_l_per_100km.toFixed(1) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
